// DealMarker.jsx
import React, { useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { createCustomIcon, createPinIcon } from './mapUtils';
import { findClosestLocations } from 'utils/geocode-util';

import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { renderToString } from 'react-dom/server';
import L from 'leaflet';

const toSentenceCase = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const DealMarker = React.memo(({
    facility,
    color,
    isHighlighted,
    zIndex,
    isCurrentDeal,
    hasCompsData = false, // Default to false to avoid breaking existing code
    portfolioFacilities = [], // Add portfolio facilities prop
    isPortfolioMode = false,
    isNonGroupFacility = false
}) => {
    const icon = useMemo(() =>
        createCustomIcon(color, isHighlighted, isNonGroupFacility ? false : isCurrentDeal),
        [color, isHighlighted, isCurrentDeal, isNonGroupFacility]
    );

    const position = useMemo(() => {
        // First, check for coordinates property (new format)
        if (facility.coordinates && typeof facility.coordinates === 'object') {
            // Check for latitude/longitude properties in coordinates object
            if (facility.coordinates.latitude !== undefined && facility.coordinates.longitude !== undefined) {
                const lat = parseFloat(facility.coordinates.latitude);
                const lng = parseFloat(facility.coordinates.longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return [lat, lng];
                }
            }
        }

        // Handle coordinates as array format
        if (Array.isArray(facility.coordinates) && facility.coordinates.length === 2) {
            const lat = parseFloat(facility.coordinates[0]);
            const lng = parseFloat(facility.coordinates[1]);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // If the deal has comps data, prioritize Latitude/Longitude 
        if (hasCompsData) {
            // Try capitalized Latitude/Longitude first (preferred for deals with comps)
            if (facility.Latitude !== undefined && facility.Longitude !== undefined) {
                const lat = parseFloat(facility.Latitude);
                const lng = parseFloat(facility.Longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return [lat, lng];
                }
            }
            // Try lowercase latitude/longitude 
            else if (facility.latitude !== undefined && facility.longitude !== undefined) {
                const lat = parseFloat(facility.latitude);
                const lng = parseFloat(facility.longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return [lat, lng];
                }
            }
        }

        // For all deals (including those with comps data where Latitude/Longitude wasn't found)
        // fall back to the standard lat/lng properties
        if (facility.lat !== undefined && facility.lng !== undefined) {
            const lat = parseFloat(facility.lat);
            const lng = parseFloat(facility.lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Last resort: log warning and return a default position
        console.warn("Could not determine position for facility:", facility);
        return [0, 0]; // Default position (not ideal, but prevents breaking)
    }, [facility, hasCompsData]);

    // Find closest portfolio facilities
    const closestFacilities = useMemo(() => {
        if (!portfolioFacilities || portfolioFacilities.length === 0 ||
            position[0] === 0 && position[1] === 0) {
            return [];
        }

        // Create a location object from the position
        const locationObj = {
            coordinates: position,
            facilityName: facility.Facilities
        };

        // Find closest 3 portfolio facilities, excluding the facility itself
        const otherFacilities = portfolioFacilities.filter(
            pf => pf.facilityName !== facility.Facilities
        );
        return findClosestLocations(locationObj, otherFacilities, 3);
    }, [position, facility, portfolioFacilities]);

    // Only render if we have a valid position
    if (!position || (position[0] === 0 && position[1] === 0)) {
        console.warn("Skipping marker with invalid position:", facility);
        return null;
    }

    return (
        <Marker
            position={position}
            icon={icon}
            opacity={isCurrentDeal ? 1 : isHighlighted ? 0.9 : 0.8}
            zIndexOffset={zIndex}
        >
            <Popup>
                <div className="p-2">
                    <div
                        className="w-full h-1 mb-1 rounded-full"
                        style={{ backgroundColor: color }}
                    />
                    <h3 className="font-bold text-lg mb-1">{facility.Facilities}</h3>
                    <p className="text-sm text-gray-600 mb-1">{toSentenceCase(facility.Deal)}</p>
                    {facility.Address && (
                        <p className="text-sm mb-1">{facility.Address}</p>
                    )}
                    {!isPortfolioMode && facility['City, State'] && (
                        <p className="text-sm">{facility['City, State']}</p>
                    )}
                    <div className="text-sm">
                        <p>Total Beds: {facility['Total Beds']}</p>
                        {facility['Star Rating'] && (
                            <p>Star Rating: {facility['Star Rating']}</p>
                        )}
                    </div>

                    {/* Closest Portfolio Facilities Section */}
                    {closestFacilities.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="font-semibold text-sm mb-1">Closest Portfolio Facilities:</p>
                            <ul className="text-xs space-y-1">
                                {closestFacilities.map((pf, index) => (
                                    <li key={index} className="flex justify-between">
                                        <span>
                                            {pf.facilityName}
                                            <span className="text-gray-500 ml-1">({pf.details?.operator || 'N/A'})</span>
                                        </span>
                                        <span className="font-medium ml-2">
                                            {pf.distance.toFixed(1)} mi
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Popup>
        </Marker>
    );
});


// PortfolioMarker.jsx

export const PortfolioMarker = React.memo(({
    location,
    color
}) => {
    const icon = useMemo(() =>
        createPinIcon(color || "#BFCAD2", false),
        [color]
    );

    // Added position calculation logic similar to CompetitionMarker
    const position = useMemo(() => {
        // Handle the new format where coordinates is an object with latitude/longitude
        if (location.coordinates && typeof location.coordinates === 'object') {
            // Check for latitude/longitude properties
            if (location.coordinates.latitude !== undefined && location.coordinates.longitude !== undefined) {
                const lat = parseFloat(location.coordinates.latitude);
                const lng = parseFloat(location.coordinates.longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return [lat, lng];
                }
            }
        }

        // Handle the existing array format
        if (Array.isArray(location.coordinates) && location.coordinates.length === 2) {
            const lat = parseFloat(location.coordinates[0]);
            const lng = parseFloat(location.coordinates[1]);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Fallback to lat/lng properties if they exist
        if (location.lat !== undefined && location.lng !== undefined) {
            const lat = parseFloat(location.lat);
            const lng = parseFloat(location.lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        console.warn("Portfolio facility missing valid coordinates:", location);
        return [0, 0]; // Default position to prevent breaking
    }, [location]);

    // Only render if we have a valid position
    if (!position || (position[0] === 0 && position[1] === 0)) {
        console.warn("Skipping portfolio marker with invalid position:", location);
        return null;
    }

    return (
        <Marker
            position={position}
            icon={icon}
            opacity={0.8}
            zIndexOffset={-1000} // Changed from 500 to -1000
        >
            <Popup>
                <div className="p-2">
                    <div
                        className="w-full h-1 mb-2 rounded-full"
                        style={{ backgroundColor: color }}
                    />
                    <h3 className="font-bold text-lg mb-1">{location.facilityName}</h3>
                    <p className="text-sm text-gray-600 mb-2">{location.details.operator}</p>
                    <p className="text-sm mb-1">{location.city}, {location.state}</p>
                </div>
            </Popup>
        </Marker>
    );
});

// CompetitionMarker.jsx
export const CompetitionMarker = React.memo(({
    facility,
    color
}) => {
    const markerColor = "#f02d1c";

    // Create a custom icon using the PiMapPinLineDuotone from react-icons
    const icon = useMemo(() => {
        // Render the React icon to an HTML string
        const iconHtml = renderToString(
            <HiOutlineBuildingOffice2
                color={markerColor}
                style={{
                    fontSize: "30px",
                    filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.5))"
                }}
            />
        );

        // Create a Leaflet divIcon with the rendered HTML
        return L.divIcon({
            className: "competition-marker",
            html: iconHtml,
            iconSize: [30, 30],
            iconAnchor: [15, 30],
            popupAnchor: [0, -30]
        });
    }, [markerColor]);

    // Safely get position from coordinates - handle multiple format possibilities
    const position = useMemo(() => {
        // Handle array coordinates
        if (Array.isArray(facility.coordinates) && facility.coordinates.length === 2) {
            const lat = parseFloat(facility.coordinates[0]);
            const lng = parseFloat(facility.coordinates[1]);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Handle capitalized Latitude/Longitude (which is what the data actually has)
        if (facility.Latitude !== undefined && facility.Longitude !== undefined) {
            const lat = parseFloat(facility.Latitude);
            const lng = parseFloat(facility.Longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Handle lat/lng properties
        if (facility.lat !== undefined && facility.lng !== undefined) {
            // Make sure coordinates are numbers, not strings
            const lat = parseFloat(facility.lat);
            const lng = parseFloat(facility.lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Handle lowercase latitude/longitude properties
        if (facility.latitude !== undefined && facility.longitude !== undefined) {
            // Make sure coordinates are numbers, not strings
            const lat = parseFloat(facility.latitude);
            const lng = parseFloat(facility.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Handle lat/long properties
        if (facility.lat !== undefined && facility.long !== undefined) {
            // Make sure coordinates are numbers, not strings
            const lat = parseFloat(facility.lat);
            const lng = parseFloat(facility.long);
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        // Handle string "lat,lng" format which might be in the data
        if (typeof facility.coordinates === 'string' && facility.coordinates.includes(',')) {
            console.log("Parsing string coordinates:", facility.coordinates);
            const [latStr, lngStr] = facility.coordinates.split(',');
            const lat = parseFloat(latStr.trim());
            const lng = parseFloat(lngStr.trim());
            if (!isNaN(lat) && !isNaN(lng)) {
                return [lat, lng];
            }
        }

        console.warn("Competition facility missing valid coordinates:", facility);
        return [0, 0]; // Return default position instead of undefined
    }, [facility]);

    // Don't render a marker for the main facility since it's already shown as a deal marker
    if (facility.type === 'main') {
        return null;
    }

    // Only render if we have a valid position
    if (!position || (position[0] === 0 && position[1] === 0)) {
        return null;
    }

    return (
        <Marker
            position={position}
            icon={icon}
            opacity={0.85}
            zIndexOffset={4500} // Between deal markers and portfolio markers
        >
            <Popup>
                <div className="p-2">
                    <div
                        className="w-full h-1 mb-2 rounded-full"
                        style={{ backgroundColor: markerColor }}
                    />
                    <h3 className="font-bold text-lg mb-1">
                        {facility.name || facility.Facility || facility.facilityName || 'Competition Facility'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                        {facility.type === 'main' ? 'Main Facility' : 'Competition Facility'}
                    </p>


                    {facility['Star Rating'] && (
                        <p className="text-sm mb-1">Star Rating: {facility['Star Rating']}</p>
                    )}

                    {facility.Owner && facility.Owner !== "" && (
                        <p className="text-sm mb-1">Owner: {facility.Owner}</p>
                    )}
                </div>
            </Popup>
        </Marker>
    );
});